/**
 * How much of the window the on-screen keyboard is covering.
 *
 * Two jobs while someone is typing: hide the tab bar, which otherwise sits on
 * top of the composer, and pin the app to the part of the window that is
 * still visible, so the composer ends up directly above the keyboard the way
 * a messaging app's does.
 *
 * The reference is a viewport-unit probe rather than `innerHeight`, because
 * `innerHeight` on iOS is the layout viewport and moves with the toolbar. The
 * difference between a fixed `svh` and the visible area is the keyboard and
 * nothing else.
 *
 * A threshold, because the visible area also shrinks by a few pixels for
 * reasons that are not a keyboard, and hiding the navigation for those would
 * be worse than ignoring them.
 */
const ENOUGH = 120;

export function watchKeyboard(): () => void {
  const vv = window.visualViewport;
  if (!vv) return () => {};

  // The probe has to measure the same thing the shell is sized by, or the
  // difference it reports is not the keyboard. From the home screen the
  // shell is `dvh`, because `svh` there reserves room for a toolbar that
  // does not exist — and an `svh` probe against a `dvh` app under-reads by
  // exactly that strip, which was enough to keep the keyboard invisible to
  // this whole file.
  const home = (navigator as { standalone?: boolean }).standalone === true
    || (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches);
  const probe = document.createElement('div');
  probe.style.cssText = `position:fixed;top:0;left:0;width:0;height:100${home ? 'd' : 's'}vh;` +
                        'pointer-events:none;visibility:hidden';
  document.body.append(probe);

  const root = document.documentElement;
  let last = '';

  const measure = () => {
    const svh = probe.getBoundingClientRect().height || vv.height;
    const covered = Math.round(Math.max(0, svh - vv.height));
    const kb = covered > ENOUGH ? covered : 0;

    // Follow the visible area rather than compute it. The first attempt
    // shrank the app to `svh` minus the keyboard and left it where it was;
    // iOS then scrolled the visual viewport down to reveal the focused field,
    // and the screen showed the empty region below a correctly-sized app.
    // Pinned to `offsetTop` with the visible height, it cannot end up
    // somewhere the phone is not looking.
    //
    // The sanity check is the difference between a wrong number and a blank
    // screen: a visible area under 200px is not a keyboard, it is a bad read.
    const usable = kb > 0 && vv.height > 200;
    if (usable) {
      root.style.setProperty('--vv-top', `${Math.round(vv.offsetTop)}px`);
      root.style.setProperty('--vv-h', `${Math.round(vv.height)}px`);
    } else {
      root.style.removeProperty('--vv-top');
      root.style.removeProperty('--vv-h');
    }

    const now = usable ? `${kb}:${Math.round(vv.offsetTop)}:${Math.round(vv.height)}` : '0';
    if (now === last) return;
    last = now;
    root.classList.toggle('kb-open', usable);
  };

  measure();
  vv.addEventListener('resize', measure);
  vv.addEventListener('scroll', measure);
  window.addEventListener('orientationchange', measure);
  return () => {
    vv.removeEventListener('resize', measure);
    vv.removeEventListener('scroll', measure);
    window.removeEventListener('orientationchange', measure);
    probe.remove();
    root.style.removeProperty('--vv-top');
    root.style.removeProperty('--vv-h');
    root.classList.remove('kb-open');
  };
}
